import { HttpParams } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject, DestroyRef ,
  ChangeDetectionStrategy,
} from '@angular/core';
import { first, Subscription } from 'rxjs';
import { S3FolderManagerInput } from '../../admin/components/s3-folder-manager/s3-folder-manager.component';
import { S3FolderManagerService } from '../../admin/components/s3-folder-manager/s3-folder-manager.service';
import { ActionService } from '../../shared/_services/action.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  generalConfigFromGlobal,
  rowPayloadFromActiveScreen,
} from '../../shared/workspace-element-context';
import { SubmissionService } from '../submission-data/submission.service';

import { safeJsonInput } from '../../shared/_utils/safe-json-input';
interface EfileReviewAttachmentUploadInput extends S3FolderManagerInput {
  isReadOnlyMode: boolean;
  isProdSubmissionUser: boolean;
  submissionId: string;
  irsSubmissionId: string;
  ssoId: string;
}

@Component({
    selector: 'gtw-efile-review-attachment-upload',
    templateUrl: './review-attachment-upload.component.html',
    styleUrls: ['./review-attachment-upload.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileReviewAttachmentUpload implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  loadingAttachments = false;

  private readonly subs = new Subscription();
  /** Parsed from legacy CE attribute `efile-review-attachment-upload-input`. */
  private legacyHostInput: any = null;
  private inputReady = false;

  efileReviewAttachmentUploadInput!: EfileReviewAttachmentUploadInput;

  /**
   * Legacy AngularJS host (`upload-review-attachments.html`) passes the full
   * config JSON on this attribute — same shape as EfileElementsController.js
   * L1207-1218.
   */
  @Input('efile-review-attachment-upload-input')
  set efileReviewAttachmentUploadInputAttr(value: string | EfileReviewAttachmentUploadInput) {
    try {
      const parsed = typeof value === 'string' ? safeJsonInput<any>(value, null, 'review-attachment-upload.parse1') : value;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        this.legacyHostInput = parsed;
        this.buildReviewAttachmentInput();
      }
    } catch {
      console.log('efile-review-attachment-upload-input not yet stable.');
    }
  }

  /** Overrides for Angular submit-action host (no legacy CE JSON). */
  @Input() submissionIdOverride?: string;
  @Input() irsSubmissionIdOverride?: string;
  @Input() isReadOnlyModeOverride?: boolean;

  @Output('on-efile-review-attachment-upload')
  onFileUploadEvent = new EventEmitter<any>();

  constructor(
    private actionService: ActionService,
    private alertService: GtwSnackbarService,
    private s3FolderManagerService: S3FolderManagerService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private submissionService: SubmissionService,
  ) {}

  ngOnInit(): void {
    this.buildReviewAttachmentInput();
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => this.buildReviewAttachmentInput()));
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => this.buildReviewAttachmentInput()));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private buildReviewAttachmentInput(): void {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    const host = this.legacyHostInput || {};
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const rowRaw = rowPayloadFromActiveScreen(activeScreen);
    const rowFlat =
      Array.isArray(rowRaw) ? rowRaw[0] || {} : typeof rowRaw === 'object' && rowRaw ? rowRaw : {};
    const workspaceData: any = { ...rowFlat, ...(activeScreen.data || {}) };
    const activeSubmission = this.submissionService?.activeSubmission as any;

    const clientKey = String(
      host.clientKey ??
        workspaceData.clientKey ??
        workspaceData.client_key ??
        this.gtwApp.USER_SETTINGS?.user?.client_key ??
        sessionStorage.getItem('client_key') ??
        '',
    );
    const ssoId = this.gtwApp.USER_SETTINGS?.user?.sso_id ?? '';
    const submissionId = String(
      this.submissionIdOverride ??
        host.submissionId ??
        workspaceData.submissionId ??
        workspaceData.submission_id ??
        workspaceData.SUBMISSION_ID ??
        activeSubmission?.SUBMISSION_ID ??
        '',
    );
    const irsSubmissionId = String(
      this.irsSubmissionIdOverride ??
        host.irsSubmissionId ??
        workspaceData.irsSubmissionId ??
        workspaceData.irs_submission_id ??
        workspaceData.IRS_SUBMISSION_ID ??
        '',
    );
    const prodUsers =
      this.gtwApp.SERVER_CONFIG?.clientConfig?.efile_prod_submission_users ??
      this.gtwApp.SERVER_CONFIG?.configSettings?.efile_prod_submission_users;
    const isProdSubmissionUser =
      host.isProdSubmissionUser ??
      workspaceData.isProdSubmissionUser ??
      (Array.isArray(prodUsers)
        ? prodUsers.includes(this.gtwApp.USER_SETTINGS?.user?.sso_id)
        : false);
    const isReadOnlyMode =
      this.isReadOnlyModeOverride ?? (!isProdSubmissionUser && !!irsSubmissionId);

    // Legacy CE used '...' so read-only path rules (which include /gtw/binaries)
    // do not block the fixed review-attachments folder.
    const readOnlyS3Paths = '...';

    this.efileReviewAttachmentUploadInput = {
      isReadOnlyMode,
      title: host.title || 'Upload Signed Tax Doc File',
      clientKey,
      baseUrls: {
        admin: host.baseUrls?.admin ?? gc.admin,
        api: host.baseUrls?.api ?? gc.api,
      },
      canDelete: isProdSubmissionUser && !isReadOnlyMode,
      canUploadMultipleFiles: isProdSubmissionUser && !isReadOnlyMode,
      path: `/gtw/binaries/${clientKey}/${submissionId}/review-attachments/`,
      readOnlyS3Paths,
      isProdSubmissionUser,
      submissionId,
      irsSubmissionId,
      ssoId,
    };

    if (!this.inputReady) {
      this.inputReady = true;
      this.init();
    } else {
      this.init();
    }
    this.cdr.markForCheck();
  }

  init(): void {
    this.loadReviewAttachments();
  }

  onFileDelete(file: any): void {
    file.isDeleting = true;
    const params = new HttpParams({
      fromObject: {
        sso_id: this.efileReviewAttachmentUploadInput.ssoId,
        attachment_key: file.ATTACHMENT_KEY.toString(),
      },
    });

    this.actionService
      .saveActionData(
        this.efileReviewAttachmentUploadInput.baseUrls.api,
        33325,
        +this.efileReviewAttachmentUploadInput.clientKey,
        params,
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response.callSuccess === '1') {
          this.alertService.openSnackBar('Successfully Deleted File!', 'gtw-snackbar--green');
          this.loadReviewAttachments();
        } else {
          this.alertService.openSnackBar('Failed to Delete file!', 'gtw-snackbar--red');
        }
        file.isDeleting = false;
      });
  }

  onFileUpload(uploadResponseString: string): void {
    if (!uploadResponseString) return;

    const uploadResponse: { fileName: string; callSuccess: string; filePath: string } =
      safeJsonInput<any>(uploadResponseString, null, 'review-attachment-upload.parse2');

    // Prepare-submission flow (no IRS row yet) — emit to parent like legacy
    // `efileReviewAttachmentEvents` → $rootScope (EfileElementsController.js
    // L663-676). Grid upload with an IRS id saves via 33324 and does NOT emit.
    if (!this.efileReviewAttachmentUploadInput.irsSubmissionId) {
      this.onFileUploadEvent.emit(uploadResponse);
      return;
    }

    const pathChunks = uploadResponse.filePath.split('/');

    const params = new HttpParams({
      fromObject: {
        irs_id: this.efileReviewAttachmentUploadInput.irsSubmissionId,
        sso_id: this.efileReviewAttachmentUploadInput.ssoId,
        file_key: pathChunks[pathChunks.length - 1],
      },
    });

    this.actionService
      .saveActionData(
        this.efileReviewAttachmentUploadInput.baseUrls.api,
        33324,
        +this.efileReviewAttachmentUploadInput.clientKey,
        params,
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response.callSuccess === '1') {
          this.alertService.openSnackBar('Successfully Uploaded Review Attachment', 'gtw-snackbar--green');
          this.loadReviewAttachments();
        } else {
          this.alertService.openSnackBar('Failed to Upload file!', 'gtw-snackbar--red');
        }
      });
  }

  loadReviewAttachments(): void {
    if (!this.efileReviewAttachmentUploadInput) return;

    this.loadingAttachments = true;

    const params = new HttpParams({
      fromObject: {
        sso_id: this.efileReviewAttachmentUploadInput.ssoId,
        irs_id: this.efileReviewAttachmentUploadInput.irsSubmissionId,
      },
    });
    this.actionService
      .getActionData(
        this.efileReviewAttachmentUploadInput.baseUrls.api,
        33323,
        +this.efileReviewAttachmentUploadInput.clientKey,
        params,
      )
      .pipe(first())
      .subscribe((data) => {
        this.loadingAttachments = false;
        this.s3FolderManagerService.loadAttachments(data.jsonObject || []);
        this.cdr.markForCheck();
      });
  }
}
